/**
 * Pure validation for the pre-handoff contact-info form (Decision #17,
 * 2026-07-hubspot-native-handoff.md). Client-side UX nicety ONLY — the
 * backend re-validates on `contact_info` submit and can always re-request via
 * a fresh `contact_info_requested` event with corrected guidance (see
 * contact-info-card.tsx). This module hardcodes no policy about which fields
 * are required — that comes entirely from the server-driven `ContactField[]`
 * — only the shape check applied once a field is present/required.
 */

import type { ContactField } from '@/types/chat';

/** Form values keyed by field name — always all three keys present (empty
 *  string for a field the server didn't request or the user hasn't typed
 *  yet) so callers never have to guard on `undefined`. */
export interface ContactFormValues {
  name: string;
  email: string;
  phone: string;
}

/** Per-field error message, present only for fields that failed validation. */
export type ContactFormErrors = Partial<Record<ContactField['name'], string>>;

/** Minimum digit count for a phone number to look plausible. Deliberately
 *  loose (international formats vary widely) — this is a UX nicety, not an
 *  authoritative phone-number validator; the backend owns the real check. */
const MIN_PHONE_DIGITS = 7;

/** Lenient phone character set: digits, spaces, +, parens, hyphens only. */
const PHONE_CHARS_RE = /^[0-9+()\-\s]+$/;

function isValidEmailShape(value: string): boolean {
  // Single @ with non-empty local + domain parts. Deliberately not a full
  // RFC 5322 validator — see module doc: this is a UX nicety, backend
  // re-validates authoritatively.
  const at = value.indexOf('@');
  if (at <= 0 || at !== value.lastIndexOf('@')) return false;
  const local = value.slice(0, at);
  const domain = value.slice(at + 1);
  return local.length > 0 && domain.length > 0;
}

function fieldLabel(name: ContactField['name']): string {
  switch (name) {
    case 'name':
      return 'Name';
    case 'email':
      return 'Email';
    case 'phone':
      return 'Phone';
  }
}

/**
 * Validate `values` against the server-driven `fields` policy.
 *
 * Only validates fields the server actually asked for — a field NOT present
 * in `fields` is never checked, even if `values` happens to carry something
 * for it (e.g. a stale value left over from a prior, differently-shaped
 * contact_info_requested event).
 *
 * Returns a map of field name → error message; a field with no entry is
 * valid (including every optional field left blank).
 */
export function validateContactForm(
  fields: ContactField[],
  values: ContactFormValues
): ContactFormErrors {
  const errors: ContactFormErrors = {};

  for (const field of fields) {
    const raw = values[field.name] ?? '';
    const trimmed = raw.trim();

    if (trimmed.length === 0) {
      if (field.required) {
        errors[field.name] = `${fieldLabel(field.name)} is required.`;
      }
      continue; // optional + empty is valid; nothing further to check
    }

    if (field.name === 'email' && !isValidEmailShape(trimmed)) {
      errors[field.name] = 'Enter a valid email address.';
    } else if (field.name === 'phone') {
      const digitCount = trimmed.replace(/[^0-9]/g, '').length;
      if (!PHONE_CHARS_RE.test(trimmed) || digitCount < MIN_PHONE_DIGITS) {
        errors[field.name] = 'Enter a valid phone number.';
      }
    }
  }

  return errors;
}
